import { format, isAfter, isValid, parse as parseDate } from 'date-fns';
import dayjs from 'dayjs';
import { parse, ParsedQs } from 'qs';

export const FORMATTED_PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/g;
export const FORMATTED_PHONE_REGEX_UK = /^\d{5} \d{6}$/g;
export const ZIP_CODE_REGEX = /^\d{5}$/;

export const formatNumberUk = (
  rawNumber: string | number | readonly string[]
): string | null => {
  const validInput = rawNumber && typeof rawNumber === 'string';
  if (!validInput) return null;

  let digitString = rawNumber.replace(/\D/g, '');
  if (digitString.length > 5) {
    digitString = digitString.slice(0, 5) + ' ' + digitString.slice(5);
  }
  return digitString;
};

export const formattedNumber = (
  number: string | number | readonly string[]
): string => {
  const unformattedNumber = (formattedNumber) => {
    if (typeof formattedNumber === 'string') {
      const num = parseInt(formattedNumber.replace(/\D/g, ''), 10);
      return !num ? '' : num;
    }
    return '';
  };

  if (!number || typeof number !== 'string') return null;

  number = unformattedNumber(number).toString();

  if (number === 'NaN') {
    return '';
  }

  const firstThree = number.substr(0, 3);
  const midThree = number.substr(3, 3);
  const lastFour = number.substr(6, 4);

  if (number.length < 4) {
    return number;
  } else if (number.length < 7) {
    return `(${firstThree}) ${midThree}`;
  }
  return `(${firstThree}) ${midThree}-${lastFour}`;
};

export const normalizedNumber = (number: string): string => {
  if (!number) return null;
  if (new RegExp(FORMATTED_PHONE_REGEX).test(formattedNumber(number)))
    return number.replaceAll(/[() -]/g, '');
  return number;
};

export const buildFullAddress = (
  line1: string,
  line2: string,
  separator = '#'
): string => {
  if (line2) {
    return `${line1} ${separator}${line2}`;
  }
  return line1;
};

export const isValidNumber = (value: unknown): boolean => {
  return typeof value === 'number' && !!(value || value === 0);
};

export const getUploadLabel = (
  minimumUploads: unknown,
  maximumUploads: unknown
): string => {
  if (isValidNumber(minimumUploads) && isValidNumber(maximumUploads))
    return ' (Upload ' + (minimumUploads + '-' + maximumUploads) + ')';
  if (isValidNumber(minimumUploads)) return ' (Upload ' + minimumUploads + ')';
  return '';
};

export const getUrlQueryParams = (routerAsPath: string): ParsedQs => {
  const queryString = routerAsPath.split('?')[1] ?? '';

  return parse(queryString);
};

export const truncateString = (str: string, num: number): string => {
  if (str.length <= num) {
    return str;
  }
  return str.slice(0, num) + '...';
};

export const truncateFileName = (str: string, num: number): string => {
  if (str.length <= num) {
    return str;
  }
  const extension = str.split('.').pop();
  return str.slice(0, num - extension.length - 1) + '...' + extension;
};

export function checkEmailValid(email: string): boolean {
  const emailRegex = /^\w+([.+-]*\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  return emailRegex.test(email);
}

export const capitalize = (str = ''): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const capitalizeWords = (str: string): string => {
  return str
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getDisplayNumber = (rawPhone?: string, isUkUser = false) => {
  if (!isUkUser) return rawPhone;

  return formatNumberUk(rawPhone);
};

export const getIsValidUkDate = (rawDate?: string) => {
  if (!rawDate) return false;

  const parsedDate = parseDate(rawDate, 'dd/MM/yyyy', new Date());

  const isValidDate =
    isValid(parsedDate) &&
    format(parsedDate, 'dd/MM/yyyy') === rawDate &&
    !isAfter(parsedDate, new Date());

  return isValidDate;
};

export const apiDateToUkDate = (apiDate?: string): string => {
  if (!apiDate) return null;

  const parsedDate = parseDate(apiDate, 'yyyy-MM-dd', new Date());

  const isValidDate =
    isValid(parsedDate) && format(parsedDate, 'yyyy-MM-dd') === apiDate;

  if (!isValidDate) return null;

  return format(parsedDate, 'dd/MM/yyyy');
};

export const ukDateToApiDate = (ukDate?: string) => {
  if (!ukDate) return null;

  const parsedDate = parseDate(ukDate, 'dd/MM/yyyy', new Date());

  const isValidDate =
    isValid(parsedDate) && format(parsedDate, 'dd/MM/yyyy') === ukDate;

  if (!isValidDate) return null;

  return format(parsedDate, 'yyyy-MM-dd');
};

export const formatSpolightViewPin = (
  rawPin?: string | number | readonly string[]
) => {
  if (!rawPin) return null;
  if (!(typeof rawPin === 'string')) return null;
  return rawPin.replace(/[^0-9-]/g, '');
};

export const getIsValidUkPhoneNumber = (rawPhoneInput: string): boolean => {
  if (!rawPhoneInput) return false;

  return new RegExp(FORMATTED_PHONE_REGEX_UK).test(
    formatNumberUk(rawPhoneInput)
  );
};

export const bgLastUpdatedToUk = (dateString?: string) => {
  if (!dateString) return dateString;

  const parsedDate = parseDate(dateString, 'MM/dd/yy h:mm a', new Date());

  const isValidDate = isValid(parsedDate);

  if (!isValidDate) return dateString;

  return format(parsedDate, 'dd/MM/yyyy h:mm a');
};

export const bookingDateToUk = (
  dateString?: string,
  customFormat = 'EEE, d MMM'
) => {
  if (!dateString) return dateString;

  const parsedDate = parseDate(dateString, 'yyyy-MM-dd', new Date());

  const isValidDate = isValid(parsedDate);

  if (!isValidDate) return dateString;

  return format(parsedDate, customFormat);
};

export const formatCallTime = (apiDateTime?: string) => {
  if (!apiDateTime) return apiDateTime;
  const rawResult = dayjs(apiDateTime).format('h:mm A');
  return rawResult === INVALID_DATE_DAY_JS ? apiDateTime : rawResult;
};

export const cancelDateToUk = (dateString?: string) => {
  if (!dateString) return dateString;

  const rawResult = dayjs(dateString).format('ddd, D MMM. h:mm A');
  return rawResult === INVALID_DATE_DAY_JS ? dateString : rawResult;
};

export const currentDateToUk = (dateString?: string) => {
  if (!dateString) return dateString;

  const rawResult = dayjs(dateString).format('D/M/YYYY h:mm A');
  return rawResult === INVALID_DATE_DAY_JS ? dateString : rawResult;
};

export const INVALID_DATE_DAY_JS = 'Invalid Date';

export const removeCharacterFromString = (str: string, character: string) => {
  if (typeof str !== 'string') return str;
  return str.split(character).join('');
};

export const removeCommasFromString = (str: string) => {
  return removeCharacterFromString(str, ',');
};

export const getOnlyDigitsFromString = (str: string) => {
  if (typeof str !== 'string') return '';
  return str.replace(/\D/g, '');
};
